import React, { useEffect, useRef, useState, useMemo } from "react";
import "./Places.scss";
import { Col, Form } from "react-bootstrap";
import { config } from "../../../config";
import Autocomplete from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import { useLocalization } from '../../../contexts/LocalizationContext';
import * as validate from "../helper";
import mexico from "../../../constants/mx-states";
import netherlands from "../../../constants/netherlands";
import belgium from "../../../constants/belgium";
interface AddressFormState {
  pincode: string;
  address: string;
  line2: string;
  cityValue?: string;
  stateValue?: string;
  county?: string;
  countryName: string;
  countryValue: string;
  brand?: string;
}

export interface iAddessProps {
  isVerify: boolean;
  isThanks: boolean;
  validationErrors: Record<string, string>;
  isBillingReadonly: boolean;
  isShippingReadonly: boolean;
  billingAddr: any;
  formType: "billing" | "shipping"; // New prop for form type
  onPlaceUpdate: (// Callback for sending data to parent
    data: AddressFormState,
    formType: "billing" | "shipping"
  ) => void;
  onUserAddressChange?: () => void; // Callback for user-initiated address changes
  onAddressBlur?: (fieldName: string, value: string) => void; // Callback for address field blur
  brand: string;
}
const Places: React.FC<iAddessProps> = ({
  isThanks,
  isVerify,
  validationErrors,
  isBillingReadonly,
  isShippingReadonly,
  billingAddr,
  formType,
  onPlaceUpdate,
  onUserAddressChange,
  onAddressBlur,
  brand
}) => {
  const isReadonly = formType === "billing" ? isBillingReadonly : isShippingReadonly;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const autoCompleteUsedRef = useRef<boolean>(false);  // Track if autocomplete was used
  const apiKey = config.mapKey;
  const { t } = useTranslation();
  const { locale, stateDropdown, countryList, countyField, states } = useLocalization();

  // Sort country list alphabetically by name (case-insensitive) without mutating original
  const sortedCountryList = useMemo(() => {
    return [...countryList].sort((a, b) => 
      a.countryName.toLowerCase().localeCompare(b.countryName.toLowerCase())
    );
  }, [countryList]);

  const [selectedCountryCode, setSelectedCountryCode] = useState(sortedCountryList[0].ISO_CC);
  const [addressState, setAddressState] = useState<AddressFormState>({
    pincode: "",
    address: "",
    line2: "",
    cityValue: locale === 'en-sg' ? "Singapore" : "",
    ...((stateDropdown || locale === 'zh-tw') && { stateValue: locale === 'zh-tw' ? "TW" : "" }),
    ...(countyField && { county: "" }),
    countryName: sortedCountryList[0].countryName,
    countryValue: sortedCountryList[0].ISO_CC,
  });

  // Dynamic states based on selected country for nl-nl locale
  const allowedStates = useMemo(() => {
    let stateList;
    
    if (brand === 'MSI' && locale === 'en-us') {
      stateList = states?.filter(state => state.code !== 'AK' && state.code !== 'HI');
    } else if (locale === 'nl-nl') {
      // For nl-nl locale, use dynamic states based on selected country
      if (addressState.countryValue === 'NL') {
        stateList = netherlands;
      } else if (addressState.countryValue === 'BE') {
        stateList = belgium;
      } else {
        stateList = states;
      }
    } else {
      stateList = states;
    }
    
    // Sort alphabetically by name (case-insensitive) without mutating original
    if (stateList && stateList.length > 0) {
      return [...stateList].sort((a, b) => 
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
    }
    
    return stateList;
  }, [brand, locale, states, addressState.countryValue]);


  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute("autoComplete", "none");
    }
  });

  const handlePlaceSelect = (place: any) => {
    const addressComponents = place.address_components;

    let newPincode = locale === 'en-hk' ? "00000" : addressState.pincode || "";
    let newAddress = "";
    let geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
    let geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
    let geoCountyValue = addressState.county || "";
    let geoCountryValue = addressState.countryValue;
    let geoCountryName = addressState.countryName;

    if (addressComponents) {
      // Handle Google Places autocomplete selection
      let streetNumber = "";
      let route = "";
      let premise = "";
      let neighborhood = "";
      let sublocality = "";

      for (const component of addressComponents) {
        if (component.types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (component.types.includes("premise")) {
          premise = component.long_name;
        }
        if (component.types.includes("route")) {
          route = component.long_name;
        }
        if (component.types.includes("neighborhood")) {
          neighborhood = (newAddress ? ", " : "") + component.long_name;
        }
        if (component.types.includes("sublocality") || component.types.includes("sublocality_level_1") || component.types.includes("sublocality_level_2")) {
          if (locale === 'en-ph') {
            // Barangay mapping
            neighborhood = component.long_name;
          } else if (selectedCountryCode.toLowerCase() === 'cz') {
            geoCityValue = component.long_name;
          } else {
            sublocality = (newAddress ? ", " : "") + component.long_name;
          }
        }
        if (selectedCountryCode.toLowerCase() !== 'us' && component.types.includes("postal_town")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("locality")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          if (locale === 'zh-tw' || locale === 'ko-kr') {
            geoCityValue = component.long_name;
          } else {
            // ✅ United States or default case using dropdown validation
            if (stateDropdown) {
              const stateCode = component.short_name;
              // Verify if the state code exists in geoStates
              const validState = allowedStates?.find(state => state.code === stateCode);
              if (validState) {
                geoStateValue = stateCode
              }
            }
            if (locale === 'zh-tw') {
              geoStateValue = 'TW'
            }
            if (locale === 'en-ph' && component.short_name === "NCR") {
              geoStateValue = component.short_name; // Province short code (PH)
            }
            // ✅ Special handling for Mexican states
            if (locale === 'es-mx' || component.short_name.endsWith('.')) {
              const normalized = mexico.find(mx =>
                mx.name.toLowerCase().replace(/\s|\(.*?\)/g, '') === component.long_name.toLowerCase().replace(/\s|\(.*?\)/g, '')
              );
              if (normalized) {
                geoStateValue = normalized.code;
              }
            }
          }
        }
        // ✅ Special handling for Brazilian cities
        if (component.types.includes("administrative_area_level_2") && (locale === 'en-me' || locale === 'pt-br' || locale === 'en-ph')) {
          if (locale === 'en-ph') {
            geoStateValue = component.short_name; // Province short code (PH)
          } else {
            geoCityValue = component.long_name;
          }
        }
        if (component.types.includes("country")) {
          const countryCode = component.short_name.toUpperCase();
          const validCountry = sortedCountryList.find(country => country.ISO_CC === countryCode);
          if (validCountry) {
            geoCountryValue = countryCode;
            setSelectedCountryCode(countryCode);
            geoCountryName = validCountry?.countryName || "";
          }
        }
          if (component.types.includes("postal_code")) {
            if (locale === 'nl-nl' && addressState.countryValue === 'BE') {
              newPincode = component.long_name.startsWith('B-') ? component.long_name : `B-${component.long_name}`;
            } else {
              newPincode = component.long_name;
            }
          }
        // 🔹 Hong Kong-specific handling
        if (geoCountryValue === "HK") {
          // Highest priority: neighborhood, then sublocality, then locality
          if (component.types.includes("neighborhood")) {
            geoCityValue = component.long_name; // e.g., "Soho"
          } else if (component.types.includes("sublocality_level_1")) {
            geoCityValue = component.long_name; // e.g., "Tsim Sha Tsui"
          } else if (component.types.includes("locality")) {
            geoCityValue = component.long_name; // e.g., "Kowloon"
          }

          if (locale === 'en-hk') {
            newPincode = "00000";
          }
        }
      }
      // Combine street number and route to create the address
      if (streetNumber || route || premise) {
        // Filter out empty strings and join with single space
        newAddress = [streetNumber, premise, route]
          .filter(part => part && part.trim())
          .join(" ");
      } else if (streetNumber) {
        newAddress = streetNumber;
      } else if (route) {
        newAddress = route;
      } else if (premise) {
        newAddress = premise;
      }

      // Append neighborhood and sublocality for Middle East locale when both exist
      if (locale === 'en-me' && neighborhood && sublocality) {
        newAddress = (newAddress ? newAddress + ", " : "") + `${neighborhood}, ${sublocality}`;
      }

      if (!(newAddress?.length > 0) || !(validate.validateStringOrIntegerAddress(newAddress, locale))) {
        newAddress = place.formatted_address.split(",")[0] || "";
        if (locale === 'ja-jp') {
          const parts = place.formatted_address.split(",");
          if (parts[0].indexOf("Japan") > -1) {
            newAddress = parts[parts.length - 2] + parts[parts.length - 1];
          } else {
            newAddress = parts[0] + parts[1];
          }
        } else if (locale !== 'es-es' && locale !== 'it-it' && locale !== 'en-gb') {
          newAddress = place.formatted_address.split(",")[0] || "";
        } else {
          newAddress = place.formatted_address.split(",")[0] + place.formatted_address.split(",")[1];
        }
      }
    } else if (place.target) {
      // Handle form field changes
      newPincode = locale === 'en-hk' ? "00000" : addressState.pincode || "";
      newAddress = addressState.address || "";
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      geoCountyValue = addressState.county || "";
      const { name, value } = place.target;
      // Reset location-specific fields when country changes
      newPincode = locale === 'en-hk' ? "00000" : addressState.pincode || "";
      newAddress = addressState.address || "";
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      geoCountyValue = addressState.county || "";
      geoCountryValue = selectedCountryCode || "";
      geoCountryName = sortedCountryList.find(country => country.ISO_CC === selectedCountryCode)?.countryName || "";

      if (name === `countryValue${formType === "billing" ? "Billing" : "Shipping"}`) {
        // Handle country dropdown change
        const selectedCountry = sortedCountryList.find(country => country.ISO_CC === value);
        if (selectedCountry) {
          geoCountryValue = selectedCountry.ISO_CC;
          geoCountryName = selectedCountry.countryName;

          if (locale === 'en-eu' || locale === 'de-de' || locale === 'nl-nl' || locale === 'fr-fr') {
            // Reset location-specific fields when country changes
            newPincode = "";
            newAddress = "";
            geoCityValue = "";
            geoStateValue = "";
            geoCountyValue = "";
          }

          // Update selected country code for Places API

          setSelectedCountryCode(value === 'ME' ? 'AE' : value);

          // Reset Google Places input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }
      } else if (name === `address${formType === "billing" ? "Billing" : "Shipping"}`) {
        newAddress = value.split(",")[0];
      } else if (name === `city${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoCityValue = value;
      } else if (name === `stateName${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoStateValue = locale === 'zh-tw' ? "TW" : value;
      } else if (name === `county${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoCountyValue = value;
      } else if (name === `zip${formType === "billing" ? "Billing" : "Shipping"}`) {
        newPincode = value;
        if (locale === 'en-hk') {
          newPincode = "00000";
        }
      }
    }

    // Update address state
    const newAddressState = {
      ...addressState,
      pincode: newPincode,
      address: newAddress,
      cityValue: geoCityValue,
      ...((locale === 'zh-tw' || stateDropdown) && { stateValue: locale === 'zh-tw' ? "TW" : geoStateValue }),
      ...(countyField && { county: geoCountyValue }),
      countryName: geoCountryName,
      countryValue: geoCountryValue,
    };

    setAddressState(newAddressState);
  };

  // Wrapper function for user-initiated address selection (Autocomplete onPlaceSelected)
  const handleUserPlaceSelected = (place: any) => {
    if (formType === "shipping" && onUserAddressChange) {
      onUserAddressChange();
    }
    // Mark that autocomplete was used so we can trigger validation after state update
    autoCompleteUsedRef.current = true;
    handlePlaceSelect(place);
  };

  // Wrapper function for user-initiated address typing (Autocomplete onChange)
  const handleUserAddressChange = (event: any) => {
    if (formType === "shipping" && onUserAddressChange) {
      onUserAddressChange();
    }
    handlePlaceSelect(event);
  };

  // Handler for address field blur - triggers validation
  const handleAddressFieldBlur = (event: any) => {
    if (onAddressBlur) {
      // Use correct field name format for validation: addressBilling or addressShipping
      const fieldName = formType === "billing" ? "addressBilling" : "addressShipping";
      const value = event.target?.value || addressState.address;
      onAddressBlur(fieldName, value);
    }
  };

  // Handler for city field blur - triggers validation
  const handleCityBlur = (event: any) => {
    handlePlaceSelect(event);
    if (onAddressBlur) {
      const fieldName = "cityValue";
      const value = event.target?.value || addressState.cityValue;
      onAddressBlur(fieldName, value);
    }
  };

  // Handler for state field blur - triggers validation
  const handleStateBlur = (event: any) => {
    handlePlaceSelect(event);
    if (onAddressBlur) {
      const fieldName = "stateValue";
      const value = event.target?.value || addressState.stateValue;
      onAddressBlur(fieldName, value);
    }
  };

  // Handler for zip/postal code field blur - triggers validation
  const handleZipBlur = (event: any) => {
    handlePlaceSelect(event);
    if (onAddressBlur) {
      const fieldName = "pincode";
      const value = event.target?.value || addressState.pincode;
      onAddressBlur(fieldName, value);
    }
  };

  // Handler for country field blur - triggers validation
  const handleCountryBlur = (event: any) => {
    handlePlaceSelect(event);
    if (onAddressBlur) {
      const fieldName = "countryValue";
      const value = event.target?.value || addressState.countryValue;
      onAddressBlur(fieldName, value);
    }
  };

  // Handler for line2 blur - triggers validation (US character limit)
  const handleLine2Blur = (event: any) => {
    if (onAddressBlur) {
      const fieldName = formType === "shipping" ? "line2Shipping" : "line2Billing";
      const value = event?.target?.value ?? addressState.line2 ?? "";
      onAddressBlur(fieldName, value);
    }
  };

  useEffect(() => {
    let addrData: any = {
      ...{ pincode: locale === 'en-hk' ? "00000" : addressState.pincode },
      ...{ address: addressState.address },
      ...{ line2: addressState.line2 },
      ...{ cityValue: addressState.cityValue },
      ...((stateDropdown || locale === 'zh-tw') && { stateValue: locale === 'zh-tw' ? "TW" : addressState.stateValue }),
      ...(countyField && { county: addressState.county }),
      ...{ countryName: addressState.countryName },
      ...{ countryValue: addressState.countryValue },
    }

    if (!addressState.pincode && !addressState.address && !addressState.cityValue && !addressState.stateValue) {
      addrData = {}
    };
    if (Object.keys(addrData).length > 0) {
      onPlaceUpdate(addrData, formType);
    }

    // Trigger validation for auto-filled fields if autocomplete was used
    if (autoCompleteUsedRef.current && onAddressBlur) {
      // Small delay to ensure state is fully updated
      setTimeout(() => {
        // Validate address line 1
        const addressFieldName = formType === "billing" ? "addressBilling" : "addressShipping";
        if (addressState.address) {
          onAddressBlur(addressFieldName, addressState.address);
        }
        // Validate city if it was auto-filled
        if (addressState.cityValue) {
          onAddressBlur("cityValue", addressState.cityValue);
        }
        // Validate state if it was auto-filled
        if (addressState.stateValue) {
          onAddressBlur("stateValue", addressState.stateValue);
        }
        // Validate zip if it was auto-filled
        if (addressState.pincode && locale !== 'en-hk') {
          onAddressBlur("pincode", addressState.pincode);
        }
        // Validate country if it was auto-filled
        if (addressState.countryValue) {
          onAddressBlur("countryValue", addressState.countryValue);
        }
        // Reset the flag
        autoCompleteUsedRef.current = false;
      }, 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);
  useEffect(() => {
    if (billingAddr) {
      const {
        postalCode,
        line1,
        city,
        countrySubdivision,
        county,
        countryName,
        country,
        line2,
      } = JSON.parse(billingAddr);
      setAddressState({
        ...addressState,
        pincode: locale === 'en-hk' ? "00000" : postalCode,
        address: line1,
        line2: line2,
        cityValue: locale === 'en-sg' ? "Singapore" : city,
        stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
        county: county || '',
        countryName: sortedCountryList.find(c => c.ISO_CC === country)?.countryName || "",
        countryValue: country,
      });

      onPlaceUpdate(
        {
          pincode: locale === 'en-hk' ? "00000" : postalCode,
          address: line1,
          line2: line2,
          cityValue: locale === 'en-sg' ? "Singapore" : city,
          stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
          county: county || '',
          countryName: countryName,
          countryValue: country,
        },
        formType
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddr, formType]);
  return (
    <div className={`Places ${formType === "billing" ? "billing" : "shipping"}`}>
      <Col md={6}><Form.Group controlId={`address${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`address${formType === "billing" ? "Billing" : "Shipping"}`}>{t("ADDRESS1_COLON")}</Form.Label>
        {locale === 'vi-vn' ? (
          <Form.Control
            id={`address${formType === "billing" ? "Billing" : "Shipping"}`}
            placeholder={t("ADDRESS1_REQUIRED")}
            className={`form-control ${validationErrors[`${formType}address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            type="text"
            autoComplete="none"
            name={`address${formType === "billing" ? "Billing" : "Shipping"}`}
            readOnly={isReadonly}
            value={addressState.address}
            isInvalid={!!validationErrors[`${formType}address`]}
            onChange={handlePlaceSelect}
            onBlur={handleAddressFieldBlur}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}address`]}
            aria-describedby={validationErrors[`${formType}address`] ? `address${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          />
        ) : (
          config && (
            <Autocomplete
              id={`address${formType === "billing" ? "Billing" : "Shipping"}`}
              name={`address${formType === "billing" ? "Billing" : "Shipping"}`}
              className={`form-control ${validationErrors[`${formType}address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
              ref={inputRef}
              placeholder={t("ADDRESS1_REQUIRED")}
              apiKey={apiKey}
              onPlaceSelected={handleUserPlaceSelected}
              onBlur={handleAddressFieldBlur}
              onChange={handleUserAddressChange}
              options={{
                types: ["geocode", "establishment"],
                componentRestrictions: { country: selectedCountryCode },
              }}
              defaultValue={addressState.address}
              value={addressState.address}
              autoComplete="new-password"
              readOnly={isReadonly}
              disabled={isReadonly}
              inputProps={{
                ref: inputRef,
                autoComplete: 'none'
              }}
              aria-required="true"
              aria-invalid={!!validationErrors[`${formType}address`]}
              aria-describedby={validationErrors[`${formType}address`] ? `address${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
            />
          )
        )}
        {validationErrors[`${formType}address`] && (
          <Form.Control.Feedback type="invalid" id={`address${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}address`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
      <Col md={6}><Form.Group controlId={`line2${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`line2${formType === "billing" ? "Billing" : "Shipping"}`}>{t("LINE2_OPTIONAL_COLON")}</Form.Label>
        <Form.Control
          id={`line2${formType === "billing" ? "Billing" : "Shipping"}`}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          placeholder={t("LINE2_OPTIONAL")}
          className={`form-control ${validationErrors[`${formType}line2`] ? "is-invalid" : ""} ${isReadonly ? "disabled" : ""} `}
          name={`line2${formType === "billing" ? "Billing" : "Shipping"}`}
          value={addressState.line2}
          isInvalid={!!validationErrors[`${formType}line2`]}
          aria-invalid={!!validationErrors[`${formType}line2`]}
          aria-describedby={validationErrors[`${formType}line2`] ? `line2${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          onChange={(event: any) => {
            setAddressState({
              ...addressState,
              line2: event.target.value,
            })
          }}
          onBlur={handleLine2Blur}
        />
        {validationErrors[`${formType}line2`] && (
          <Form.Control.Feedback type="invalid" id={`line2${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}line2`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
      {locale !== 'en-sg' && (<Col md={6}><Form.Group controlId={`city${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`city${formType === "billing" ? "Billing" : "Shipping"}`}>{t("CITY_COLON")}</Form.Label>
        <Form.Control
          id={`city${formType === "billing" ? "Billing" : "Shipping"}`}
          placeholder={locale === 'en-sg' ? t("CITY") : t("CITY_REQUIRED")}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`city${formType === "billing" ? "Billing" : "Shipping"}`}
          value={addressState.cityValue}
          isInvalid={!!validationErrors[`${formType}cityValue`]}
          onChange={handlePlaceSelect}
          onBlur={handleCityBlur}
          aria-required="true"
          aria-invalid={!!validationErrors[`${formType}cityValue`]}
          aria-describedby={validationErrors[`${formType}cityValue`] ? `city${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
        />
        {validationErrors[`${formType}cityValue`] && (
          <Form.Control.Feedback type="invalid" id={`city${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}cityValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>)}

      {(stateDropdown) && (
        <Col md={6}>
          <Form.Group controlId={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}>
            <Form.Label className="visually-hidden" htmlFor={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}>{t("STATE")}</Form.Label>
            <Form.Select
              id={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}
              autoComplete="none"
              value={addressState.stateValue}
              disabled={isReadonly}
              className={`form-control  ${isReadonly ? "disabled" : ""} `}
              name={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}
              onChange={handlePlaceSelect}
              onBlur={handleStateBlur}
              isInvalid={!!validationErrors[`${formType}stateValue`]}
              aria-required="true"
              aria-invalid={!!validationErrors[`${formType}stateValue`]}
              aria-describedby={validationErrors[`${formType}stateValue`] ? `stateName${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
            >
              <option value="">{t("SELECT_STATE")}</option>
              {allowedStates?.map((geoState, index) => (
                <option key={index} value={geoState.code}>
                  {geoState.name}
                </option>
              ))}
            </Form.Select>
            {validationErrors[`${formType}stateValue`] && (
              <Form.Control.Feedback type="invalid" id={`stateName${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
                {validationErrors[`${formType}stateValue`]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>)}
      {locale !== 'en-hk' && locale !== 'en-me' && (<Col md={6}>
        <Form.Group controlId={`zip${formType === "billing" ? "Billing" : "Shipping"}`}>
          <Form.Label className="visually-hidden" htmlFor={`zip${formType === "billing" ? "Billing" : "Shipping"}`}>{t("ZIP_CODE_COLON")}</Form.Label>
          <Form.Control
            id={`zip${formType === "billing" ? "Billing" : "Shipping"}`}
            placeholder={t("ZIP_CODE_REQUIRED")}
            type="text"
            autoComplete="none"
            readOnly={isReadonly}
            className={`form-control  ${isReadonly ? "disabled" : ""} `}
            name={`zip${formType === "billing" ? "Billing" : "Shipping"}`}
            value={addressState.pincode}
            isInvalid={!!validationErrors[`${formType}pincode`]}
            onChange={handlePlaceSelect}
            onBlur={handleZipBlur}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}pincode`]}
            aria-describedby={validationErrors[`${formType}pincode`] ? `zip${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          />
          {validationErrors[`${formType}pincode`] && (
            <Form.Control.Feedback type="invalid" id={`zip${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
              {validationErrors[`${formType}pincode`]}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>)}
      <Col md={6}><Form.Group controlId={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
      >
        <div className={`${sortedCountryList.length === 1 ? "singlecountry" : ""}`}>
          <Form.Label className="visually-hidden" htmlFor={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}>{t("COUNTRY_REQUIRED")}</Form.Label>
          <Form.Select
            id={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
            autoComplete="none"
            className={`form-control  ${isReadonly || sortedCountryList.length === 1 ? "disabled" : ""} `}
            name={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
            disabled={isReadonly || sortedCountryList.length === 1}
            value={addressState.countryValue}
            onChange={handlePlaceSelect}
            onBlur={handleCountryBlur}
            isInvalid={!!validationErrors[`${formType}countryValue`]}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}countryValue`]}
            aria-describedby={validationErrors[`${formType}countryValue`] ? `countryValue${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          >

            {sortedCountryList.map((country) => (
              <option key={country.ISO_CC} value={country.ISO_CC}>
                {country.countryName}
              </option>
            ))}

          </Form.Select>
        </div>
        {validationErrors[`${formType}countryValue`] && (
          <Form.Control.Feedback type="invalid" id={`countryValue${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}countryValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
    </div>
  );
};
export default Places;